import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ActiaMapService } from 'src/app/core/services/actia-map.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/core/services/api.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { ConfirmationModalComponent } from 'src/app/shared/components/confirmation-modal/confirmation-modal.component';
import { GEOFENCING_BY_CUSTOMER_ENDPOINT, TRACKING_FIELDS_ENDPOINT } from 'src/app/core/config/config.constants';
import { DxColorBoxComponent } from 'devextreme-angular';
import { DatePipe } from '@angular/common';

// Array dei giorni della settimana per la logica di scheduling
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const datepipe: DatePipe = new DatePipe('en-US');

@Component({
  selector: 'app-geofence-panel',
  templateUrl: './geofence-panel.component.html',
  styleUrls: ['./geofence-panel.component.scss']
})
export class GeofencePanelComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  // Input properties
  @Input() isOpened: boolean = false;
  @Input() visibleGeofences: any[] = [];
  @Input() writeGeofenceGrant: boolean = false;
  @Input() fieldsList: any[] = [];
  @Input() listVehiclesInfo: any[] = [];
  @Input() timespanSelectBoxItems: any[] = [];
  @Input() daysOfWeek: any[] = [];
  @Input() radioData: any[] = [];
  @Input() hoursFormat: string = 'HH:mm';
  @Input() dateTimeFormatAmPm: string = 'dd/MM/yyyy HH:mm';
  @Input() selectedCustomerUidSelector$: any; // Customer selezionato

  // Output events
  @Output() closePanel = new EventEmitter<void>();

  // ViewChild references
  @ViewChild(DxColorBoxComponent) colorBox: DxColorBoxComponent;
  @ViewChild('validator') validator: any;

  // Component properties
  public geofenceDraw: any = {};
  public selectedItems: any[] = [];
  public isVisibleGeo: boolean = true;
  public isField: boolean = false;
  public fieldsSelected: any = null;
  public currentTimespanGeofence: string = '';
  public showDaily: boolean = false;
  public showPeriod: boolean = false;
  public notValidWeek: boolean = false;
  public fromTime: any = new Date();
  public toTime: any = new Date();
  public fromDateValue: any = new Date();
  public toDateValue: any = new Date();
  public today: Date = new Date();
  public geoLayer: any = null;
  public editMode: boolean = false;

  // Time string properties for PrimeNG time inputs
  public fromTimeString: string = '00:00';
  public toTimeString: string = '00:30';

  // Tracking delle modifiche per ogni geofence
  private geofenceModifications: Map<string, boolean> = new Map();
  
  // Backup dei valori originali per ogni geofence in modifica
  private geofenceBackups: Map<string, any> = new Map();

  // ViewChild references per i date picker
  @ViewChild('fromDate') fromDate: any;
  @ViewChild('toDate') toDate: any;

  // Istanze dei controlli date per il scheduling
  public fromDateHourInstance: any;
  public toDateHourInstance: any;

  // Formato data senza secondi per il display
  public dateTimeFormatNoSecond: string = 'dd/MM/yyyy HH:mm';

  // Subscriptions management
  private subscriptions: Subscription = new Subscription();

  // FontAwesome icons
  public faWarning = faWarning;

  constructor(
    private translate: TranslateService,
    private mapService: ActiaMapService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
    private apiService: ApiService,
    private utilsService: UtilsService
  ) {
    this.setGeofenceTranslations();
  }

  ngOnInit(): void {
    console.log('ZAK in geofance veicle list (ngOnInit)', this.listVehiclesInfo);
    this.initializeComponent();
    this.initializeTimeStrings();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Log dei cambiamenti degli input
    if (changes['listVehiclesInfo']) {
      console.log('ZAK listVehiclesInfo changed:', changes['listVehiclesInfo'].currentValue);
    }

    // Controlla se è cambiato il customer
    if (changes['selectedCustomerUidSelector$']) {
      this.handleCustomerChange(changes['selectedCustomerUidSelector$']);
    }

    // Gestisce l'apertura del pannello
    if (changes['isOpened']) {
      if (this.isOpened) {
        console.log('📖 Geofence panel opened');
        console.log('ZAK in geofance veicle list (panel opened)', this.listVehiclesInfo);
        // Non caricare qui i geofence, sono già stati caricati dal servizio
        // quando è stato fatto il primo click sul toggle

        // Aggiorna i colori quando il pannello viene aperto
        setTimeout(() => {
          this.updateGeofenceColors();
        }, 200);
      } else {
        console.log('📖 Geofence panel closed');
      }
    }
  }

  ngOnDestroy(): void {
    // Pulisce tutte le subscriptions
    this.subscriptions.unsubscribe();
    console.log('🧹 Geofence panel destroyed, subscriptions cleaned up');
  }

  private initializeComponent(): void {
    console.log('🔄 Inizializzazione componente geofence-panel...');

    // Initialize any necessary data
    this.setGeofenceTranslations();
    this.setupGeofenceSubscriptions();

    // NON caricare automaticamente i geofence all'inizializzazione
    // I geofence verranno caricati solo quando necessario tramite il toggle
    console.log('📋 Geofence panel inizializzato, in attesa dell\'apertura...');
  }

  /**
   * Gestisce il cambiamento del customer
   */
  private handleCustomerChange(customerChange: any): void {
    const previousCustomer = customerChange.previousValue;
    const currentCustomer = customerChange.currentValue;

    // Controlla se c'è un customer valido
    if (!currentCustomer?.uid) {
      console.log('❌ Customer non valido o non selezionato');
      this.visibleGeofences = [];
      return;
    }

    // Se è cambiato il customer UID, ricarica i geofence
    if (!previousCustomer || previousCustomer.uid !== currentCustomer.uid) {
      console.log(`🔄 Customer cambiato: ${previousCustomer?.uid || 'nessuno'} → ${currentCustomer.uid}`);
      this.visibleGeofences = [];
      this.loadGeofencesFromService();
    }
  }

  /**
   * Imposta le subscriptions permanenti per i geofence
   */
  private setupGeofenceSubscriptions(): void {
    // Sottoscrivi ai cambiamenti dei geofence per aggiornamenti in tempo reale
    const geofenceChangeSubscription = this.mapService.changeGeofence.subscribe((loaded: boolean) => {
      if (loaded) {
        // Aggiorna sempre i dati quando i geofence vengono caricati
        this.visibleGeofences = [...this.mapService.visibleGeofences];

        // Formatta gli schedule per ogni geofence
        const self = this;
        this.visibleGeofences.forEach(function (visibleGeofence) {
          visibleGeofence.schedule = self.formatSchedules(visibleGeofence);
        });

        console.log('🔄 Geofence aggiornati dal servizio:', this.visibleGeofences.length);

        // Assicurati che i drawConf siano parsati correttamente
        this.ensureDrawConfParsed();

        // Aggiorna i colori dei bordi con più tentativi per assicurarsi che il DOM sia pronto
        setTimeout(() => {
          this.updateGeofenceColors();
        }, 150);

        setTimeout(() => {
          this.updateGeofenceColors();
        }, 500);

        setTimeout(() => {
          this.updateGeofenceColors();
        }, 1000);
      }
    });

    // Sottoscrivi ai cambiamenti dei layer per aggiornamenti della mappa
    const geofenceLayerSubscription = this.mapService.changeGeofenceLayer.subscribe((layer: any) => {
      if (layer) {
        console.log('🗺️ Layer geofence aggiornato:', layer);
      }
    });

    // Sottoscrivi agli eventi di disegno
    const polygonCreatedSubscription = this.mapService.onPolygonCreated.subscribe((layer: any) => {
      this.onPolygonDrawn(layer);
    });

    // Sottoscrivi agli eventi di modifica delle forme
    const shapeEditedSubscription = this.mapService.onShapeEdited.subscribe((event: any) => {
      this.onPolygonEdited(event);
    });

    // Aggiungi le subscriptions a quelle gestite
    this.subscriptions.add(geofenceChangeSubscription);
    this.subscriptions.add(geofenceLayerSubscription);
    this.subscriptions.add(polygonCreatedSubscription);
    this.subscriptions.add(shapeEditedSubscription);
  }

  /**
   * Carica i geofence utilizzando il nuovo metodo del servizio
   */
  private loadGeofencesFromService(): void {
    if (!this.selectedCustomerUidSelector$?.uid) {
      console.warn('❌ No customer UID available for loading geofences');
      this.visibleGeofences = [];
      return;
    }

    console.log(`🔄 Loading geofences for customer: ${this.selectedCustomerUidSelector$.uid}`);
    this.spinner.show();

    // Imposta il customer selezionato nel servizio
    this.mapService.selectedCustomerUidSelector$ = this.selectedCustomerUidSelector$;

    // Chiama il metodo getGeofence() del servizio per caricare i geofence
    this.mapService.getGeofence();

    // Ascolta i cambiamenti dei geofence per aggiornare l'interfaccia
    const geofenceSubscription = this.mapService.changeGeofence.subscribe((loaded: boolean) => {
      if (loaded) {
        // Aggiorna i dati del componente con quelli del servizio
        this.visibleGeofences = [...this.mapService.visibleGeofences];

        // Formatta gli schedule per ogni geofence
        const self = this;
        this.visibleGeofences.forEach(function (visibleGeofence) {
          visibleGeofence.schedule = self.formatSchedules(visibleGeofence);
        });

        console.log('🔄 Geofence caricati dal servizio:', this.visibleGeofences);
        console.log(`✅ Caricati ${this.visibleGeofences.length} geofences dal servizio`);
        this.spinner.hide();

        // Aggiorna i colori dei bordi dopo il caricamento
        setTimeout(() => {
          this.updateGeofenceColors();
        }, 300);

        // Rimuovi la subscription temporanea dopo aver ricevuto i dati
        geofenceSubscription.unsubscribe();
      }
    });

    // Gestione timeout per evitare spinner infinito
    setTimeout(() => {
      this.spinner.hide();
      console.warn('⚠️ Timeout caricamento geofence');
    }, 10000); // 10 secondi timeout
  }

  // UI Helper methods
  isGeofenceSelected(geofence: any): boolean {
    return this.selectedItems.some(item => item.uid === geofence.uid);
  }

  trackByGeofenceUid(index: number, geofence: any): string {
    return geofence.uid;
  }

  /**
   * Applica dinamicamente il colore del bordo per ogni geofence
   */
  ngAfterViewInit(): void {
    // Applica i colori dei bordi dopo che la vista è stata inizializzata
    setTimeout(() => {
      this.updateGeofenceColors();
    }, 100);
  }

  /**
   * Assicura che tutti i geofence abbiano il drawConf parsato correttamente
   */
  private ensureDrawConfParsed(): void {
    console.log('🔧 Controllo e parsing drawConf per tutti i geofence...');

    this.visibleGeofences.forEach(geofence => {
      if (typeof geofence.drawConf === 'string') {
        try {
          geofence.drawConf = JSON.parse(geofence.drawConf);
          console.log('✅ DrawConf parsato per:', geofence.name, 'Colore:', geofence.drawConf?.color);
        } catch (error) {
          console.warn('⚠️ Errore parsing drawConf per:', geofence.name, error);
          geofence.drawConf = { color: '#FF0000', mode: 'Poly' };
        }
      }

      // Assicurati che ci sia sempre un colore
      if (!geofence.drawConf?.color) {
        if (!geofence.drawConf) {
          geofence.drawConf = {};
        }
        geofence.drawConf.color = '#FF0000';
        console.log('🔧 Colore di default assegnato a:', geofence.name);
      }
    });
  }

  private updateGeofenceColors(): void {
    console.log('🎨 Aggiornamento colori bordi accordion per', this.visibleGeofences.length, 'geofence');

    this.visibleGeofences.forEach(geofence => {
      const color = geofence.drawConf?.color || '#FF0000';
      console.log(`🎨 Applicazione colore ${color} per geofence:`, geofence.name, 'UID:', geofence.uid);

      const element = document.querySelector(`.geofence-tab-${geofence.uid} .p-accordion-header`);
      if (element) {
        (element as HTMLElement).style.setProperty('--geofence-color', color);
        console.log(`✅ Colore ${color} applicato con successo per:`, geofence.name);
      } else {
        console.warn(`⚠️ Elemento DOM non trovato per geofence:`, geofence.name, `Selector: .geofence-tab-${geofence.uid} .p-accordion-header`);
      }
    });
  }

  /**
   * Verifica se un geofence deve essere disabilitato (quando un altro è aperto e modificato)
   */
  isGeofenceDisabled(geofence: any): boolean {
    // Se non ci sono elementi selezionati, nessuno è disabilitato
    if (this.selectedItems.length === 0 || !this.editMode) {
      return false;
    }

    // Se questo geofence è quello selezionato, non è disabilitato
    if (this.isGeofenceSelected(geofence)) {
      return false;
    }

    // Se c'è un altro geofence selezionato E siamo in modalità edit, questo è disabilitato
    // Questo mantiene gli altri disabilitati finché non viene fatto Save/Undo/Delete
    return this.editMode;
  }



  onClosePanel(): void {
    console.log('❌ Chiusura pannello geofence');

    // Esce dalla modalità edit se attiva
    this.exitEditMode();

    // Reset selections
    this.selectedItems = [];

    // Emit close event to parent
    this.closePanel.emit();
  }

  viewGeofenceOnMap(geofence: any): void {
    console.log('🗺️ Visualizzazione geofence sulla mappa:', geofence.name);
    this.focusGeofenceOnMap(geofence);
  }

  /**
   * Centra la mappa sul geofence selezionato
   */
  private focusGeofenceOnMap(geofence: any): void {
    if (!this.mapService || !geofence) {
      return;
    }

    try {
      // Se il geofence ha coordinate, calcola il centro
      if (geofence.geometry?.coordinates) {
        const coordinates = geofence.geometry.coordinates;
        let bounds: any = null;

        // Gestisce diversi tipi di geometria
        if (geofence.geometry.type === 'Polygon' && Array.isArray(coordinates[0])) {
          // Per poligoni, usa il primo anello
          const polygon = coordinates[0];
          bounds = this.calculateBounds(polygon);
        } else if (geofence.geometry.type === 'LineString' && Array.isArray(coordinates)) {
          // Per linee
          bounds = this.calculateBounds(coordinates);
        }

        if (bounds) {
          // Centra la mappa sui bounds del geofence
          const map = this.mapService.getMap();
          if (map) {
            const leafletBounds: [[number, number], [number, number]] = [
              [bounds.minLat, bounds.minLng],
              [bounds.maxLat, bounds.maxLng]
            ];
            map.fitBounds(leafletBounds, {
              padding: [20, 20],
              maxZoom: 16
            });
            console.log('🎯 Mappa centrata sul geofence:', geofence.name);
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Errore nel centrare la mappa sul geofence:', error);
    }
  }

  /**
   * Calcola i bounds di un array di coordinate GeoJSON [lng,lat]
   */
  private calculateBounds(coordinates: number[][]): { minLat: number, maxLat: number, minLng: number, maxLng: number } | null {
    if (!coordinates || coordinates.length === 0) {
      return null;
    }

    // Coordinate GeoJSON sono in formato [lng, lat]
    let minLng = coordinates[0][0];  // longitude
    let maxLng = coordinates[0][0];
    let minLat = coordinates[0][1];  // latitude
    let maxLat = coordinates[0][1];

    coordinates.forEach(coord => {
      const [lng, lat] = coord;
      minLng = Math.min(minLng, lng);  // longitude
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);  // latitude
      maxLat = Math.max(maxLat, lat);
    });

    console.log('🎯 Bounds corretti calcolati:', { minLat, maxLat, minLng, maxLng });
    return { minLat, maxLat, minLng, maxLng };
  }

  /**
   * Centra la mappa su un geofence con gestione corretta delle coordinate
   */
  private centerMapOnGeofence(geofence: any): void {
    if (!geofence.geometry?.coordinates) {
      return;
    }

    const map = this.mapService.getMap();
    if (!map) {
      return;
    }

    try {
      const coordinates = geofence.geometry.coordinates;
      // Per poligoni, usa il primo anello
      const coordsToUse = Array.isArray(coordinates[0]) ? coordinates[0] : coordinates;

      // Le coordinate sono già in formato GeoJSON [lng,lat]
      console.log('📍 Coordinate GeoJSON per centratura:', coordsToUse.slice(0, 3));

      const bounds = this.calculateBounds(coordsToUse);
      if (bounds) {
        const leafletBounds: [[number, number], [number, number]] = [
          [bounds.minLat, bounds.minLng],
          [bounds.maxLat, bounds.maxLng]
        ];

        console.log('🎯 Bounds calcolati:', bounds);
        console.log('🗺️ Leaflet bounds:', leafletBounds);
        console.log('📍 Coordinate usate pentru bounds:', coordsToUse.slice(0, 3));

        map.fitBounds(leafletBounds, {
          padding: [20, 20],
          maxZoom: 16
        });

        console.log('✅ Mappa centrata sul geofence trasformato');
      }
    } catch (error) {
      console.error('❌ Errore nel centrare la mappa:', error);
    }
  }

  editGeofence(geofence: any): void {
    console.log('✏️ Modifica geofence:', geofence.name);
    console.log('🔒 Altri accordion verranno disabilitati fino a Save/Undo/Delete');

    // Crea un backup dei valori originali prima di iniziare le modifiche
    if (!geofence.isNew) {
      this.createGeofenceBackup(geofence);
    }

    // Attiva la modalità edit
    this.geofenceDraw = geofence;
    this.editMode = true;
    this.selectedItems = [geofence];

    // Caso speciale: se è un geofence di importazione campo
    if (geofence.isImportField) {
      console.log('📂 Modalità importazione campo attivata - in attesa selezione campo');
      // Reset della selezione precedente per evitare conflitti
      this.fieldsSelected = null;

      // Forza lo stato dell'accordion dopo un breve delay
      setTimeout(() => {
        this.selectedItems = [geofence];
        this.editMode = true;
        console.log('🔒 Stato accordion forzato per importazione campo');
      }, 50);

      return;
    }

    // Per geofence normali o trasformati, abilita l'editing
    if (geofence.geometry) {
      const editableLayer = this.mapService.enableGeofenceEditing(geofence);
      if (editableLayer) {
        this.geoLayer = editableLayer;
        console.log('✅ Poligono reso editabile sulla mappa');
      }
    }

    // Imposta i valori dei campi per l'editing
    if (geofence.vehicles) {
      // Gestisce la selezione dei veicoli esistenti
    }
    if (geofence.schedule && geofence.schedule.length > 0) {
      // Gestisce la schedulazione esistente
    }
  }

  // Getter per il colore del geofence draw
  getGeofenceDrawColor(): string {
    return this.geofenceDraw?.drawConf?.color || '#FF0000';
  }

  // Verifica se un geofence specifico è in modalità edit
  isInEditMode(geofence: any): boolean {
    const isSelected = this.geofenceDraw && this.geofenceDraw.uid === geofence.uid;
    const hasEditMode = this.editMode;
    const hasSelectedItems = this.selectedItems.length > 0;
    const isImportField = geofence?.isImportField;

    // Per i geofence di importazione campo, considera sempre in edit mode se selezionato
    if (isImportField && isSelected) {
      return true;
    }

    return isSelected && hasEditMode && hasSelectedItems;
  }

  /**
   * Verifica se ci sono state modifiche ai dati del geofence
   * @param geofence Il geofence da controllare
   * @returns true se ci sono modifiche, false altrimenti
   */
  hasGeofenceChanges(geofence: any): boolean {
    // Controlla prima se ha modifiche non salvate nella mappa
    if (this.hasUnsavedChanges(geofence.uid)) {
      return true;
    }

    // Se è un nuovo geofence (non ancora salvato), considera sempre come modificato
    if (geofence.isNew) {
      return true;
    }

    // Se è il geofence attualmente selezionato per l'editing
    if (this.geofenceDraw && this.geofenceDraw.uid === geofence.uid) {
      // Se è un campo importato e selezionato, considera come modificato
      if (geofence.isImportField && this.isGeofenceSelected(geofence)) {
        return true;
      }

      // Se è in modalità edit con elementi selezionati sulla mappa
      if (this.editMode && this.selectedItems.length > 0) {
        return true;
      }

      // Se c'è un layer di editing attivo
      if (this.geoLayer) {
        return true;
      }
    }

    return false;
  }

  // Toggle tra modalità Edit e Save
  toggleEditSave(geofence: any): void {
    if (this.isInEditMode(geofence)) {
      // È in modalità edit, quindi salva
      this.performSave(geofence);
    } else {
      // Non è in modalità edit, quindi attiva edit
      this.editGeofence(geofence);
    }
  }

  // Metodo per salvare il geofence
  private performSave(geofence: any): void {
    console.log('💾 Salvataggio geofence:', geofence.name);

    // Chiama il metodo di salvataggio esistente
    this.openSavePopup(new Event('click'), geofence);
  }

  // Metodo per uscire dalla modalità edit senza salvare
  exitEditMode(): void {
    console.log('🚪 Uscita dalla modalità edit - riabilitazione di tutti gli accordion');

    // Disabilita l'editing del poligono sulla mappa
    this.mapService.disableGeofenceEditing();

    // Se c'è un geofence in editing, mantieni il suo stato di modifica
    // (non pulire le modifiche qui, solo quando si fa esplicitamente "Annulla" o "Salva")
    
    this.geofenceDraw = {};
    this.editMode = false;
    this.selectedItems = [];
    this.geoLayer = null;

    console.log('✅ Tutti gli accordion riabilitati dopo uscita dalla modalità edit');
  }

  /**
   * Metodo per annullare le modifiche e uscire dalla modalità edit
   * @param event L'evento di click
   * @param geofence Il geofence specifico di cui annullare le modifiche (opzionale)
   */
  undoEditMode(event: Event, geofence?: any): void {
    event.stopPropagation();
    console.log('🖱️ CLICK UNDO BUTTON - Geofence:', geofence?.name, 'UID:', geofence?.uid);
    console.log('↩️ Annullamento modifiche e uscita dalla modalità edit');

    // Usa il geofence passato come parametro o quello attualmente in editing
    const targetGeofence = geofence || this.geofenceDraw;

    if (!targetGeofence) {
      console.warn('⚠️ Nessun geofence da annullare trovato');
      return;
    }

    // Se è un geofence nuovo (non salvato), rimuovilo dalla lista
    if (targetGeofence.isNew) {
      console.log('🗑️ Rimozione geofence nuovo non salvato:', targetGeofence.name);
      this.visibleGeofences = this.visibleGeofences.filter(g => g.uid !== targetGeofence.uid);

      // Rimuovi il poligono dalla mappa se presente
      this.mapService.removeAllPolygonsGeofence();

      // Ridisegna tutti i geofence rimanenti sulla mappa
      this.redrawAllGeofences();
    } else if (targetGeofence.uid) {
      // Se è un geofence esistente, ripristina i valori originali e rimuovi le modifiche
      console.log('🔄 Ripristino valori originali per geofence esistente:', targetGeofence.name);
      this.cancelGeofenceModifications(targetGeofence.uid);
      
      // Ridisegna il geofence sulla mappa con i valori ripristinati
      if (targetGeofence.geometry) {
        // Disabilita l'editing corrente
        this.mapService.disableGeofenceEditing();
        
        // Ridisegna il geofence con i valori originali
        setTimeout(() => {
          this.mapService.drawPolygonGeofence(targetGeofence, false, false, true);
        }, 100);
      }
    }

    // Esce dalla modalità edit e chiude l'accordion
    this.exitEditMode();

    console.log('✅ Annullamento completato con ripristino valori originali');
  }

  /**
   * Ridisegna tutti i geofence sulla mappa
   */
  private redrawAllGeofences(): void {
    this.visibleGeofences.forEach(geofence => {
      if (!geofence.isNew) {
        this.mapService.drawPolygonGeofence(geofence, false, false, true);
      }
    });
  }

  openSavePopup(event: Event, geofence: any): void {
    event.stopPropagation();
    console.log('�️ CLICK SAVE BUTTON - Geofence:', geofence?.name, 'UID:', geofence?.uid);
    console.log('�💾 Apertura popup salvataggio geofence:', geofence);

    // Usa il geofence passato come parametro invece di this.geofenceDraw
    if (geofence?.name) {
      // Se il geofence non è già in editing, impostalo temporaneamente
      const wasInEditMode = this.geofenceDraw?.uid === geofence.uid;
      if (!wasInEditMode) {
        this.geofenceDraw = geofence;
      }
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '500px',
        data: "global.confirmSave",
        position: { top: '40px' }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.saveFence(event, geofence);
        }
      });
    } else {
      // Se non c'è un nome, potremmo voler mostrare un messaggio di errore
      console.warn('⚠️ Nome geofence mancante per il salvataggio');
      // Valida il form per mostrare gli errori di validazione
      if (this.validator && this.validator.instance) {
        this.validator.instance.validate();
      }
    }
  }

  openDeletePopup(event: Event, geofence: any): void {
    event.stopPropagation();
    console.log('�️ CLICK DELETE BUTTON - Geofence:', geofence?.name, 'UID:', geofence?.uid);
    console.log('�🗑️ Apertura popup eliminazione geofence:', geofence);

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: "global.confirmDelete",
      position: { top: '40px' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (geofence.isNew) {
          // Se è un geofence nuovo non salvato, ricarica semplicemente i geofence
          this.mapService.getGeofence();
          // Rimuovi eventuali forme dalla mappa
          this.mapService.removeAllPolygonsGeofence();
        } else {
          // Se è un geofence esistente, chiamare l'API per eliminarlo
          this.deleteFence(event, geofence);
        }
      }
    });
  }


  // Event handlers
  addPolygon(): void {
    console.log('🎨 Inizio disegno poligono');
    this.isField = false;

    // Ottieni un colore unico basato sui geofence esistenti
    const color = this.getUniqueColorForNewGeofence();

    // Attiva la modalità di disegno poligono nel servizio mappa con il colore
    this.mapService.startPolygonDrawing(color);
  }

  /**
   * Ottiene un colore unico per un nuovo geofence basato sui colori già utilizzati
   * @returns string Colore esadecimale per il nuovo geofence
   */
  private getUniqueColorForNewGeofence(): string {
    // Palette di colori predefinita per i geofence
    const colorPalette = [
      '#FF0000', // Rosso
      '#00FF00', // Verde
      '#0000FF', // Blu
      '#FFFF00', // Giallo
      '#FF00FF', // Magenta
      '#00FFFF', // Ciano
      '#FFA500', // Arancione
      '#800080', // Viola
      '#FFC0CB', // Rosa
      '#A52A2A', // Marrone
      '#808080', // Grigio
      '#000000', // Nero
      '#4B0082', // Indaco
      '#008000', // Verde scuro
      '#FF4500', // Rosso-arancione
      '#2E8B57', // Verde mare
      '#DC143C', // Rosso cremisi
      '#4169E1', // Blu reale
      '#32CD32', // Verde lime
      '#FF1493'  // Rosa profondo
    ];

    // Raccoglie i colori già utilizzati dai geofence esistenti
    const usedColors = new Set<string>();
    this.visibleGeofences.forEach(geofence => {
      if (geofence.drawConf?.color) {
        usedColors.add(geofence.drawConf.color.toUpperCase());
      }
    });

    // Trova il primo colore disponibile nella palette
    for (const color of colorPalette) {
      if (!usedColors.has(color.toUpperCase())) {
        console.log(`🎨 Colore assegnato per nuovo geofence: ${color}`);
        return color;
      }
    }

    // Se tutti i colori della palette sono utilizzati, genera un colore casuale
    const randomColor = this.utilsService.getRandomColor();
    console.log(`🎨 Tutti i colori predefiniti utilizzati, colore casuale assegnato: ${randomColor}`);
    return randomColor;
  }

  // Drawing event handlers
  private onPolygonDrawn(layer: any): void {
    console.log('🔷 Poligono disegnato:', layer);
    this.createNewGeofenceFromLayer(layer, 'Polygon');
  }

  /**
   * Gestisce la modifica di un poligono esistente in modalità edit
   */
  private onPolygonEdited(event: any): void {
    console.log('📝 Poligono modificato in edit mode:', event);

    if (this.editMode && this.geofenceDraw) {
      // Leaflet-draw emette event.layers che è una LayerGroup
      if (event.layers) {
        event.layers.eachLayer((layer: any) => {
          // Aggiorna la geometria del geofence con le nuove coordinate
          const geoJson = layer.toGeoJSON();
          if (geoJson?.geometry) {
            this.geofenceDraw.geometry = geoJson.geometry;
            console.log('✅ Geometria del geofence aggiornata:', this.geofenceDraw.name);

            // Aggiorna anche il geoLayer per il salvataggio
            this.geoLayer = layer;
          }
        });
      }
      // Se non è un LayerGroup ma un singolo layer
      else if (event.target) {
        const geoJson = event.target.toGeoJSON();
        if (geoJson?.geometry) {
          this.geofenceDraw.geometry = geoJson.geometry;
          console.log('✅ Geometria del geofence aggiornata:', this.geofenceDraw.name);
          this.geoLayer = event.target;
        }
      }
    }
  }

  /**
   * Crea un nuovo geofence da un layer disegnato
   */
  private createNewGeofenceFromLayer(layer: any, type: string): void {
    // Converte il layer in GeoJSON
    const geoJson = layer.toGeoJSON();

    // Estrae il colore dal layer disegnato, con fallback al rosso
    const layerColor = layer.options?.color || '#FF0000';

    // Genera un nome unico per il nuovo geofence
    const geofenceCount = this.visibleGeofences.length + 1;
    const geofenceName = `Nuovo ${type} ${geofenceCount}`;

    // Crea un nuovo oggetto geofence con configurazione completa
    const newGeofence = {
      uid: this.utilsService.guid(),
      name: geofenceName,
      description: `Geofence ${type.toLowerCase()} creato automaticamente`,
      geometry: geoJson.geometry,
      isNew: true,
      vehicles: [],
      inside: true,
      drawConf: {
        mode: type === 'Polygon' ? 'Poly' : type, // Usa 'Poly' per compatibilità con il backend
        color: layerColor
      },
      schedule: []
    };

    // Aggiungi il nuovo geofence alla lista
    this.visibleGeofences.push(newGeofence);

    // Rimuovi il layer temporaneo dalla mappa se esiste
    const map = this.mapService.getMap();
    if (layer && map) {
      map.removeLayer(layer);
    }

    // Ridisegna il geofence con il tooltip del nome
    this.mapService.drawPolygonGeofence(newGeofence, true, false, true);

    // Seleziona automaticamente il nuovo geofence per la modifica
    this.editGeofence(newGeofence);

    console.log('✨ Nuovo geofence creato con nome:', geofenceName, 'colore:', layerColor, 'configurazione:', newGeofence.drawConf);
  }











  /**
   * Gestisce il toggle manuale dell'accordion quando si clicca su un elemento
   * @param geofence Il geofence su cui si è cliccato
   * @param event L'evento di click
   */
  toggleAccordionItem(geofence: any, event: Event): void {
    event.stopPropagation();

    const isCurrentlySelected = this.selectedItems.some(item => item.uid === geofence.uid);

    // Non permettere la chiusura dell'accordion per i geofence di importazione campo
    if (geofence.isImportField) {
      console.log('🔒 Accordion di importazione campo - mantieni sempre aperto:', geofence.name);
      // Assicurati che rimanga selezionato
      if (!isCurrentlySelected) {
        this.editGeofence(geofence);
      }
      return;
    }

    if (isCurrentlySelected) {
      // Se è già selezionato, chiudilo (solo per geofence normali)
      console.log('🔄 Toggle: Chiusura accordion per geofence già aperto:', geofence.name);
      this.selectedItems = [];
      this.exitEditMode();
    } else {
      // Se non è selezionato, aprilo e attiva la modalità edit
      console.log('🔄 Toggle: Apertura accordion per geofence:', geofence.name);

      // Chiama editGeofence che gestisce tutto il setup necessario
      this.editGeofence(geofence);
    }
  }

  /**
   * Gestisce l'evento di apertura/chiusura dell'accordion PrimeNG
   */
  onAccordionTabOpen(event: any, geofence: any): void {
    console.log('📖 Accordion aperto:', geofence.name);
    if (!this.isGeofenceSelected(geofence)) {
      this.editGeofence(geofence);
    }
  }

  onAccordionTabClose(event: any, geofence: any): void {
    console.log('📕 Accordion chiuso:', geofence.name);
    if (this.isGeofenceSelected(geofence)) {
      this.exitEditMode();
    }
  }

  /**
   * Gestisce il click su un tab dell'accordion
   */
  onAccordionTabClick(geofence: any, event: Event): void {
    const isCurrentlySelected = this.isGeofenceSelected(geofence);
    const isDisabled = this.isGeofenceDisabled(geofence);

    console.log('🖱️ Click su accordion tab:', geofence.name, 'Selected:', isCurrentlySelected, 'Disabled:', isDisabled);

    // Se il tab è disabilitato, non fare nulla
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Se non è selezionato, selezionalo
    if (!isCurrentlySelected) {
      this.editGeofence(geofence);
    } else {
      // Se è già selezionato e non è un campo di importazione, chiudilo
      if (!geofence.isImportField) {
        this.exitEditMode();
      }
    }
  }



  openColorBox(event: Event): void {
    event.stopPropagation();
    if (this.colorBox) {
      this.colorBox.instance.open();
    }
  }

  colorValueChanged(event: any): void {
    let idx = -1;
    if (event.value) {
      if (this.geofenceDraw.isNew) {
        idx = this.visibleGeofences.length - 1;
      } else {
        idx = _.findIndex(this.visibleGeofences, (geof) => this.geofenceDraw.uid == geof.uid);
      }

      if (this.geofenceDraw?.drawConf) {
        this.geofenceDraw.drawConf.color = event.value;
        
        // Segna il geofence come modificato
        this.markGeofenceAsModified(this.geofenceDraw.uid);
      }

      if (this.isField) {
        this.mapService.polygonGroupLayer?.setStyle({
          color: event.value
        });
      } else {
        // Aggiorna il colore del layer sulla mappa se esiste
        if (this.geoLayer?.setStyle) {
          this.geoLayer.setStyle({
            color: event.value,
            fillColor: event.value
          });
          console.log('🎨 Colore del poligono aggiornato sulla mappa:', event.value);
        }

        // Aggiorna anche l'array dei geofence visibili
        if (this.visibleGeofences[idx]?.drawConf) {
          this.visibleGeofences[idx].drawConf.color = event.value;
        }

        // Aggiorna il colore del bordo nell'accordion
        setTimeout(() => {
          this.updateSingleGeofenceColor(this.geofenceDraw.uid, event.value);
        }, 50);
      }
    }
  }

  /**
   * Aggiorna il colore del bordo per un singolo geofence
   */
  private updateSingleGeofenceColor(geofenceUid: string, color: string): void {
    const element = document.querySelector(`.geofence-tab-${geofenceUid} .p-accordion-header`);
    if (element) {
      (element as HTMLElement).style.setProperty('--geofence-color', color);
      console.log('🎨 Colore bordo accordion aggiornato per:', geofenceUid, 'con colore:', color);
    }
  }

  onValueChanged(event: any): void {
    console.log('📍 Campo selezionato per importazione:', event.value);
    this.fieldsSelected = event.value;

    if (!this.fieldsSelected) {
      return;
    }

    // Verifica se siamo in modalità importazione campo
    // Il geofence potrebbe aver perso il flag isImportField, controlliamo anche il nome
    const isImportMode = this.geofenceDraw?.isImportField ||
                        (this.geofenceDraw && this.geofenceDraw.name === 'Importa Campo') ||
                        (this.isField && this.editMode);

    if (isImportMode) {
      console.log('🔄 Trasformazione geofence importazione in geofence reale...');

      // Rimuovi solo i poligoni di campo, NON i geofence esistenti
      this.mapService.removeAllPolygonsFields();
      // NON rimuovere tutti i geofence per evitare interferenze
      // this.mapService.removeAllPolygonsGeofence();

      // Aggiorna il geofence corrente con i dati del campo
      this.geofenceDraw.name = `Campo: ${this.fieldsSelected.name}`;
      this.geofenceDraw.description = `Geofence creato dal campo ${this.fieldsSelected.name}`;

      // Le coordinate del campo potrebbero essere già in formato GeoJSON [lng,lat]
      // Verifichiamo il formato delle coordinate per evitare doppia conversione
      const fieldCoords = this.fieldsSelected.fieldGeometry.coordinates;
      console.log('📍 Coordinate originali campo:', fieldCoords[0]?.slice(0, 3));

      // Controlla se le coordinate sono in formato [lat,lng] o [lng,lat]
      // Se la prima coordinata ha lat > lng, probabilmente è [lat,lng] e va convertita
      // Se la prima coordinata ha lng > lat, probabilmente è già [lng,lat] GeoJSON
      const firstCoord = fieldCoords[0]?.[0];
      const isLatLngFormat = firstCoord && firstCoord[0] > firstCoord[1]; // lat > lng per l'Italia

      console.log('🔍 Prima coordinata:', firstCoord);
      console.log('🔍 Formato rilevato:', isLatLngFormat ? '[lat,lng] - conversione necessaria' : '[lng,lat] - già GeoJSON');

      let convertedCoords;
      if (isLatLngFormat) {
        // Converti da [lat,lng] a [lng,lat] per GeoJSON
        if (this.fieldsSelected.fieldGeometry.type === 'Polygon' && Array.isArray(fieldCoords[0])) {
          convertedCoords = fieldCoords.map((ring: number[][]) =>
            ring.map((coord: number[]) => [coord[1], coord[0]]) // [lat,lng] → [lng,lat]
          );
        } else {
          convertedCoords = fieldCoords.map((coord: number[]) => [coord[1], coord[0]]);
        }
        console.log('🔄 Coordinate convertite da [lat,lng] a [lng,lat]');
      } else {
        // Le coordinate sono già in formato GeoJSON [lng,lat], usale direttamente
        convertedCoords = fieldCoords;
        console.log('✅ Coordinate già in formato GeoJSON [lng,lat]');
      }

      this.geofenceDraw.geometry = {
        type: this.fieldsSelected.fieldGeometry.type,
        coordinates: convertedCoords
      };

      this.geofenceDraw.drawConf = {
        ...this.fieldsSelected.drawConf,
        mode: 'Poly' // Forza modalità poligono per geofence
      };
      // NON cambiare isImportField per permettere selezioni successive
      // this.geofenceDraw.isImportField = false;

      // Aggiorna anche nell'array visibleGeofences
      const index = this.visibleGeofences.findIndex(g => g.uid === this.geofenceDraw.uid);
      if (index !== -1) {
        this.visibleGeofences[index] = { ...this.geofenceDraw };
      }

      console.log('✅ Campo trasformato in geofence:', this.geofenceDraw.name);
      console.log('📍 Coordinate GeoJSON convertite:', convertedCoords[0]?.slice(0, 3));

      // Prima disabilita l'editing precedente se attivo
      if (this.geoLayer) {
        console.log('🔄 Disabilitazione editing precedente...');
        this.mapService.disableGeofenceEditing();
        this.geoLayer = null;
      }

      // Rimuovi eventuali poligoni del campo precedente
      this.mapService.removeAllPolygonsFields();

      // Forza sempre il disegno del nuovo campo con editing
      setTimeout(() => {
        console.log('🎨 Tentativo abilitazione editing per:', this.geofenceDraw.name);
        const editableLayer = this.mapService.enableGeofenceEditing(this.geofenceDraw);
        if (editableLayer) {
          this.geoLayer = editableLayer;
          console.log('✅ Geofence da campo disegnato e reso editabile');
        } else {
          console.warn('⚠️ Impossibile abilitare editing - provo disegno normale');
          // Fallback: disegna il poligono normalmente e poi prova ad abilitare editing
          this.mapService.drawPolygonGeofence(this.geofenceDraw, false, false, true);

          // Riprova l'editing dopo il disegno
          setTimeout(() => {
            const retryEditableLayer = this.mapService.enableGeofenceEditing(this.geofenceDraw);
            if (retryEditableLayer) {
              this.geoLayer = retryEditableLayer;
              console.log('✅ Editing abilitato al secondo tentativo');
            }
          }, 200);
        }
      }, 150);

      // Forza sempre l'accordion aperto per i campi importati
      // Usa setTimeout per assicurarsi che avvenga dopo il rendering
      setTimeout(() => {
        this.selectedItems = [this.geofenceDraw];
        this.editMode = true;
        console.log('🔒 Accordion forzato aperto per campo importato:', this.geofenceDraw.name);
      }, 10);

      // Assicurati che il geofence mantenga il flag di importazione
      this.geofenceDraw.isImportField = true;      // Centra la mappa con coordinate corrette
      this.centerMapOnGeofence(this.geofenceDraw);
    }
    // NON aggiungere più il fallback per evitare conflitti
  }

  manageTimespanFieldsVisibility(): void {
    switch (this.currentTimespanGeofence) {
      case 'timeperiod':
        this.showPeriod = true;
        this.showDaily = false;
        break;
      case 'daily':
        this.showPeriod = false;
        this.showDaily = true;
        break;
      default:
        this.showPeriod = false;
        this.showDaily = false;
        break;
    }
  }
 addCurfewToList() {
    let self = this;
    console.log('➕ Aggiunta curfew alla lista:', this.currentTimespanGeofence);
    
    // Segna il geofence come modificato quando aggiungi un curfew
    if (this.geofenceDraw?.uid) {
      this.markGeofenceAsModified(this.geofenceDraw.uid);
    }
    
    switch (this.currentTimespanGeofence) {
      case 'timeperiod':
        this.fromDateHourInstance = this.fromDate.instance;
        this.toDateHourInstance = this.toDate.instance;
        if (!this.geofenceDraw.schedule) {
          this.geofenceDraw.schedule = [];
        }
        this.geofenceDraw.schedule.push({
          type: 'Once',
          text: datepipe.transform(this.fromDateValue, this.dateTimeFormatNoSecond) + ' - ' + datepipe.transform(this.toDateValue, this.dateTimeFormatNoSecond),
          from: this.fromDateHourInstance.option('value'),
          to: this.toDateHourInstance.option('value'),
          timestampStart: this.utilsService.toUTC(new Date(this.fromDateHourInstance.option('value').getFullYear(),
            this.fromDateHourInstance.option('value').getMonth(),
            this.fromDateHourInstance.option('value').getDate(),
            this.fromDateHourInstance.option('value').getHours(),
            this.fromDateHourInstance.option('value').getMinutes())),
          timestampEnd: this.utilsService.toUTC(new Date(this.toDateHourInstance.option('value').getFullYear(),
            this.toDateHourInstance.option('value').getMonth(),
            this.toDateHourInstance.option('value').getDate(),
            this.toDateHourInstance.option('value').getHours(),
            this.toDateHourInstance.option('value').getMinutes(), 59, 999)),
          uid: this.utilsService.guid()
        });
        console.log('✅ Curfew periodo singolo aggiunto:', this.geofenceDraw.schedule[this.geofenceDraw.schedule.length - 1].text);
        break;
      case 'daily':
        if (this.validateDayOfWeek()) {
          if (!this.geofenceDraw.schedule) {
            this.geofenceDraw.schedule = [];
          }

          for (let day of this.daysOfWeek) {
            if (day.value) {
              this.geofenceDraw.schedule.push({
                type: 'Weekly',
                text: this.translate.instant('SHORTDAY.' + day.original).toUpperCase() + ' ' + datepipe.transform(this.fromTime, this.hoursFormat) + ' - ' + datepipe.transform(this.toTime, this.hoursFormat),
                // dayOfWeek: day.option('name'),
                from: this.fromTime,
                to: this.toTime,
                //UTC
                timestampStart: ((this.fromTime.getHours() * 3600) + (this.fromTime.getMinutes() * 60)) * 1000,
                timestampEnd: ((this.toTime.getHours() * 3600) + (this.toTime.getMinutes() * 60)) * 1000,
                dayOfWeek: DAYS.indexOf(day.original),
                uid: this.utilsService.guid()
              });
            }
          }

          /*_.each(this.daysOfWeek, function (day) {
            if (day.value) {
              self.geofenceDraw.schedule.push({
                type: 'Weekly',
                text: self.translate.instant('SHORTDAY.' + day.original).toUpperCase() + ' ' + datepipe.transform(self.fromTime, self.dateTimeFormatNoSecond) + ' - ' + datepipe.transform(self.toTime, self.dateTimeFormatNoSecond),
                // dayOfWeek: day.option('name'),
                from: self.fromTime,
                to: self.toTime,
                //UTC
                timestampStart: ((self.fromTime.getHours() * 3600) + (self.fromTime.getMinutes() * 60)) * 1000,
                timestampEnd: ((self.toTime.getHours() * 3600) + (self.toTime.getMinutes() * 60)) * 1000,
                dayOfWeek: DAYS.indexOf(day.original),
                uid: self.utilsService.guid()
              });
            }
          });*/
        }
          console.log('✅ Curfew settimanale aggiunto per i giorni selezionati');
        break;
    }
  }

  /**
   * Aggiunge un curfew per un periodo singolo (data/ora specifica)
   */
  private addSinglePeriodCurfew(): void {
    if (!this.fromDate || !this.toDate) {
      console.warn('⚠️ Riferimenti date mancanti per il periodo singolo');
      return;
    }

    this.fromDateHourInstance = this.fromDate.instance;
    this.toDateHourInstance = this.toDate.instance;

    if (!this.geofenceDraw.schedule) {
      this.geofenceDraw.schedule = [];
    }

    const fromDateTime = this.fromDateHourInstance.option('value');
    const toDateTime = this.toDateHourInstance.option('value');

    const curfew = {
      type: 'Once',
      text: datepipe.transform(fromDateTime, this.dateTimeFormatNoSecond) + ' - ' + datepipe.transform(toDateTime, this.dateTimeFormatNoSecond),
      from: fromDateTime,
      to: toDateTime,
      timestampStart: this.utilsService.toUTC(new Date(
        fromDateTime.getFullYear(),
        fromDateTime.getMonth(),
        fromDateTime.getDate(),
        fromDateTime.getHours(),
        fromDateTime.getMinutes()
      )),
      timestampEnd: this.utilsService.toUTC(new Date(
        toDateTime.getFullYear(),
        toDateTime.getMonth(),
        toDateTime.getDate(),
        toDateTime.getHours(),
        toDateTime.getMinutes(),
        59,
        999
      )),
      uid: this.utilsService.guid()
    };

    this.geofenceDraw.schedule.push(curfew);
    console.log('✅ Curfew periodo singolo aggiunto:', curfew.text);
  }

  /**
   * Aggiunge curfew settimanali per i giorni selezionati
   */
  private addWeeklyCurfew(): void {
    if (!this.validateDayOfWeek()) {
      console.warn('⚠️ Nessun giorno della settimana selezionato per il curfew settimanale');
      return;
    }

    if (!this.geofenceDraw.schedule) {
      this.geofenceDraw.schedule = [];
    }

    for (let day of this.daysOfWeek) {
      if (day.value) {
        const curfew = {
          type: 'Weekly',
          text: this.translate.instant('SHORTDAY.' + day.original).toUpperCase() +
                ' ' + datepipe.transform(this.fromTime, this.hoursFormat) +
                ' - ' + datepipe.transform(this.toTime, this.hoursFormat),
          from: this.fromTime,
          to: this.toTime,
          timestampStart: ((this.fromTime.getHours() * 3600) + (this.fromTime.getMinutes() * 60)) * 1000,
          timestampEnd: ((this.toTime.getHours() * 3600) + (this.toTime.getMinutes() * 60)) * 1000,
          dayOfWeek: DAYS.indexOf(day.original),
          uid: this.utilsService.guid()
        };

        this.geofenceDraw.schedule.push(curfew);
        console.log('✅ Curfew settimanale aggiunto per', day.original + ':', curfew.text);
      }
    }
  }

  checkBoxWeekChanged(event: any, index: number): void {
    this.daysOfWeek[index].value = event.value;
    console.log('✅ Giorno della settimana aggiornato:', this.daysOfWeek[index]);
    console.log('📅 Stato giorni settimana:', this.daysOfWeek);
    console.log('event:', event);
    this.validateDayOfWeek();
  }

  stopPropagationTextBox_radioGroup(event: Event): void {
    event.stopPropagation();
  }

  validateDayOfWeek(): boolean {
    let aDaySelected = false;
    let i: number = 0;
    while (!aDaySelected && i < 7) {
      aDaySelected = this.daysOfWeek[i].value;
      i++;
    }
    this.notValidWeek = !aDaySelected;
    console.log('📅 Validazione giorni settimana - almeno uno selezionato:', aDaySelected);
    console.log('📅 Stato validazione giorni settimana - notValidWeek:', this.notValidWeek);
    return !this.notValidWeek;
  }

  /**
   * Converte la stringa di tempo in oggetto Date
   */
  onFromTimeStringChanged(): void {
    console.log('🕐 FromTimeString cambiato:', this.fromTimeString);
    if (this.fromTimeString && this.fromTimeString.match(/^\d{2}:\d{2}$/)) {
      const [hours, minutes] = this.fromTimeString.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      this.fromTime = date;

      // Gestione automatica per l'ultimo intervallo (23:30-23:59)
      if (parseInt(hours, 10) === 23 && parseInt(minutes, 10) === 30) {
        this.toTimeString = '23:59';
        this.onToTimeStringChanged();
      }

      console.log('🕐 FromTime aggiornato:', this.fromTime);
    }
  }

  /**
   * Converte la stringa di tempo "A" in oggetto Date
   */
  onToTimeStringChanged(): void {
    console.log('🕑 ToTimeString cambiato:', this.toTimeString);
    if (this.toTimeString && this.toTimeString.match(/^\d{2}:\d{2}$/)) {
      const [hours, minutes] = this.toTimeString.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 59, 999);
      this.toTime = date;
      console.log('🕑 ToTime aggiornato:', this.toTime);
    }
  }

  /**
   * Inizializza le stringhe di tempo dai valori Date esistenti
   */
  private initializeTimeStrings(): void {
    if (this.fromTime) {
      const hours = this.fromTime.getHours().toString().padStart(2, '0');
      const minutes = this.fromTime.getMinutes().toString().padStart(2, '0');
      this.fromTimeString = `${hours}:${minutes}`;
    }

    if (this.toTime) {
      const hours = this.toTime.getHours().toString().padStart(2, '0');
      const minutes = this.toTime.getMinutes().toString().padStart(2, '0');
      this.toTimeString = `${hours}:${minutes}`;
    }
  }

  /**
   * Segna un geofence come modificato
   * @param geofenceUid UID del geofence modificato
   */
  markGeofenceAsModified(geofenceUid: string): void {
    this.geofenceModifications.set(geofenceUid, true);
    console.log('📝 Geofence segnato come modificato:', geofenceUid);
    console.log('📝 Mappa modifiche aggiornata:', Array.from(this.geofenceModifications.entries()));
  }

  /**
   * Rimuove il flag di modifica per un geofence (dopo il salvataggio)
   * @param geofenceUid UID del geofence salvato
   */
  clearGeofenceModifications(geofenceUid: string): void {
    this.geofenceModifications.delete(geofenceUid);
    console.log('💾 Flag modifiche rimosso per geofence:', geofenceUid);
  }

  /**
   * Verifica se un geofence ha modifiche non salvate
   * @param geofenceUid UID del geofence da controllare
   * @returns true se ha modifiche non salvate
   */
  hasUnsavedChanges(geofenceUid: string): boolean {
    return this.geofenceModifications.has(geofenceUid) && this.geofenceModifications.get(geofenceUid) === true;
  }

  /**
   * Ottiene tutti i geofence con modifiche non salvate
   * @returns Array di UID dei geofence modificati
   */
  getModifiedGeofences(): string[] {
    return Array.from(this.geofenceModifications.entries())
      .filter(([uid, isModified]) => isModified)
      .map(([uid]) => uid);
  }

  /**
   * Gestisce il cambiamento del nome del geofence
   * @param event Evento di cambio valore del nome
   */
  onGeofenceNameChanged(event: any): void {
    if (this.geofenceDraw?.uid && event.value !== event.previousValue) {
      console.log('📝 Nome geofence modificato:', event.value);
      this.markGeofenceAsModified(this.geofenceDraw.uid);
    }
  }

  /**
   * Gestisce il cambiamento della descrizione del geofence
   * @param event Evento di cambio valore della descrizione
   */
  onGeofenceDescriptionChanged(event: any): void {
    if (this.geofenceDraw?.uid && event.value !== event.previousValue) {
      console.log('📝 Descrizione geofence modificata:', event.value);
      this.markGeofenceAsModified(this.geofenceDraw.uid);
    }
  }

  /**
   * Gestisce il cambiamento dei veicoli associati al geofence
   * @param event Evento di cambio valore dei veicoli
   */
  onGeofenceVehiclesChanged(event: any): void {
    if (this.geofenceDraw?.uid) {
      console.log('📝 Veicoli geofence modificati:', event.value);
      this.markGeofenceAsModified(this.geofenceDraw.uid);
    }
  }

  /**
   * Gestisce il cambiamento dell'opzione inside del geofence
   * @param event Evento di cambio valore dell'opzione inside
   */
  onGeofenceInsideChanged(event: any): void {
    if (this.geofenceDraw?.uid && event.value !== event.previousValue) {
      console.log('📝 Opzione inside geofence modificata:', event.value);
      this.markGeofenceAsModified(this.geofenceDraw.uid);
    }
  }

  /**
   * Crea un backup dei valori originali di un geofence prima di iniziare le modifiche
   * @param geofence Il geofence di cui fare il backup
   */
  createGeofenceBackup(geofence: any): void {
    if (!geofence?.uid || this.geofenceBackups.has(geofence.uid)) {
      return; // Non sovrascrivere backup esistenti
    }

    // Crea una copia profonda del geofence
    const backup = {
      uid: geofence.uid,
      name: geofence.name,
      description: geofence.description,
      inside: geofence.inside,
      vehicles: geofence.vehicles ? [...geofence.vehicles] : [],
      drawConf: geofence.drawConf ? { ...geofence.drawConf } : {},
      schedule: geofence.schedule ? [...geofence.schedule] : [],
      geometry: geofence.geometry ? JSON.parse(JSON.stringify(geofence.geometry)) : null
    };

    this.geofenceBackups.set(geofence.uid, backup);
    console.log('💾 Backup creato per geofence:', geofence.name, 'UID:', geofence.uid);
  }

  /**
   * Ripristina i valori originali di un geofence dal backup
   * @param geofenceUid UID del geofence da ripristinare
   */
  restoreGeofenceFromBackup(geofenceUid: string): boolean {
    const backup = this.geofenceBackups.get(geofenceUid);
    if (!backup) {
      console.warn('⚠️ Nessun backup trovato per geofence:', geofenceUid);
      return false;
    }

    // Trova il geofence nell'array visibleGeofences
    const geofenceIndex = this.visibleGeofences.findIndex(g => g.uid === geofenceUid);
    if (geofenceIndex === -1) {
      console.warn('⚠️ Geofence non trovato in visibleGeofences:', geofenceUid);
      return false;
    }

    // Ripristina i valori dal backup
    const currentGeofence = this.visibleGeofences[geofenceIndex];
    currentGeofence.name = backup.name;
    currentGeofence.description = backup.description;
    currentGeofence.inside = backup.inside;
    currentGeofence.vehicles = [...backup.vehicles];
    currentGeofence.drawConf = { ...backup.drawConf };
    currentGeofence.schedule = [...backup.schedule];
    if (backup.geometry) {
      currentGeofence.geometry = JSON.parse(JSON.stringify(backup.geometry));
    }

    // Se è il geofence attualmente in editing, aggiorna anche geofenceDraw
    if (this.geofenceDraw?.uid === geofenceUid) {
      this.geofenceDraw.name = backup.name;
      this.geofenceDraw.description = backup.description;
      this.geofenceDraw.inside = backup.inside;
      this.geofenceDraw.vehicles = [...backup.vehicles];
      this.geofenceDraw.drawConf = { ...backup.drawConf };
      this.geofenceDraw.schedule = [...backup.schedule];
      if (backup.geometry) {
        this.geofenceDraw.geometry = JSON.parse(JSON.stringify(backup.geometry));
      }
    }

    console.log('🔄 Valori ripristinati dal backup per geofence:', backup.name);
    return true;
  }

  /**
   * Rimuove il backup di un geofence (dopo salvataggio o cancellazione definitiva)
   * @param geofenceUid UID del geofence di cui rimuovere il backup
   */
  removeGeofenceBackup(geofenceUid: string): void {
    if (this.geofenceBackups.has(geofenceUid)) {
      this.geofenceBackups.delete(geofenceUid);
      console.log('🗑️ Backup rimosso per geofence:', geofenceUid);
    }
  }

  /**
   * Annulla completamente tutte le modifiche di un geofence specifico
   * @param geofenceUid UID del geofence di cui annullare le modifiche
   */
  cancelGeofenceModifications(geofenceUid: string): void {
    // Ripristina i valori originali dal backup
    const restored = this.restoreGeofenceFromBackup(geofenceUid);
    
    if (restored) {
      // Rimuovi il flag di modifica
      this.clearGeofenceModifications(geofenceUid);
      
      // Rimuovi il backup dopo il ripristino
      this.removeGeofenceBackup(geofenceUid);
      
      console.log('✅ Modifiche annullate e valori ripristinati per geofence:', geofenceUid);
    } else {
      // Se non c'è backup, almeno rimuovi il flag di modifica
      this.clearGeofenceModifications(geofenceUid);
      console.log('⚠️ Flag modifica rimosso senza ripristino per geofence:', geofenceUid);
    }
  }

  /**
   * Rimuove un curfew dalla lista per indice
   * @param index Indice del curfew da rimuovere
   */
  removeCurfewFromList(index: number): void {
    if (this.geofenceDraw?.schedule && index >= 0 && index < this.geofenceDraw.schedule.length) {
      const removedCurfew = this.geofenceDraw.schedule.splice(index, 1)[0];
      console.log('🗑️ Curfew rimosso:', removedCurfew.text);
    }
  }

  /**
   * Pulisce tutti i curfew dalla lista
   */
  clearCurfewList(): void {
    if (this.geofenceDraw?.schedule) {
      const count = this.geofenceDraw.schedule.length;
      this.geofenceDraw.schedule = [];
      console.log(`🧹 Tutti i ${count} curfew sono stati rimossi`);
    }
  }










  /**
   * Elimina un geofence tramite API
   */
  deleteFence(e: any, geofence: any): void {
    if (!this.selectedCustomerUidSelector$?.uid) {
      console.error('❌ Nessun customer selezionato per eliminare il geofence');
      return;
    }

    console.log('🗑️ Eliminazione geofence:', geofence.uid);

    this.apiService.delete(
      GEOFENCING_BY_CUSTOMER_ENDPOINT + "/" + this.selectedCustomerUidSelector$.uid + "/geofence/" + geofence.uid
    ).subscribe({
      next: () => {
        console.log('✅ Geofence eliminato con successo');
        // Esce dalla modalità edit per riabilitare tutti gli accordion
        this.exitEditMode();
        // Ricarica i geofence dopo l'eliminazione
        this.mapService.getGeofence();
        // Rimuovi tutte le forme dalla mappa
        this.mapService.removeAllPolygonsGeofence();
      },
      error: (error) => {
        console.error('❌ Errore durante l\'eliminazione del geofence:', error);
        this.utilsService.showErrorPopup(error);
      }
    });
  }

  /**
   * Salva un geofence (crea nuovo o aggiorna esistente)
   */
  saveFence(e: any, fence: any): void {
    let self = this;

    // Rileva se è un geofence importato da campo
    const isImportedField = fence.isImportField || (fence.name && fence.name.startsWith('Campo:'));

    console.log('💾 Salvando geofence:', fence.name, 'Tipo importato da campo:', isImportedField);

    var fenceCopy: any = {
      name: fence.name,
      uid: fence.uid,
      description: fence.description,
      inside: fence.inside,
      geometry: this.geoLayer?.toGeoJSON()?.geometry,
      drawConf: JSON.stringify(fence.drawConf),
      vehicles: fence.vehicles.map(function (vehicle) {
        return {
          internalId: vehicle.internalId,
          plate: vehicle.plate,
          uid: vehicle.uid
        }
      }),
      hasCurfew: fence?.schedule?.length > 0,
      schedule: []
    };

    if (fence.isNew) {
      fenceCopy.geometry.coordinates = [fenceCopy.geometry?.coordinates];
    } else {
      if (this.geoLayer) {
        if (fence.drawConf?.mode.includes('Line')) {
          fenceCopy.geometry.coordinates = ([fenceCopy.geometry.coordinates])[0];
          fenceCopy.geometry.coordinates[0].splice(-1);
          fenceCopy.geometry.coordinates = (fenceCopy.geometry.coordinates)[0];
          fenceCopy.geometry.type = 'LineString';
        } else {
          fenceCopy.geometry.coordinates = [fenceCopy.geometry.coordinates[0]];
        }
      } else {
        fenceCopy.geometry = {}
        if (fence.drawConf?.mode.includes('Line')) {
          fenceCopy.geometry.coordinates = ([fence.geometry?.coordinates])[0];
        } else {
          // 🔧 CORREZIONE: Gestione speciale per geofence importati da campi
          if (isImportedField && fence.geometry?.coordinates) {
            // Per i geofence importati, usa le coordinate esistenti senza aggiungere array extra
            console.log('📍 Usando coordinate esistenti per geofence importato');
            fenceCopy.geometry.coordinates = fence.geometry.coordinates;
          } else {
            // Per i geofence normali, mantieni la logica esistente
            fenceCopy.geometry.coordinates = fence.geometry?.coordinates[0] ? [fence.geometry?.coordinates[0]] : [this.fieldsSelected.fieldGeometry?.coordinates[0]];
          }
        }
        fenceCopy.geometry.type = fence.geometry?.type
      }
    }

    var newSchedule: any = null;
    // var schedules = this.mergeShedules(fence.schedule);
    _.each(fence.schedule, function (schedule) {
      if (schedule.type === 'Once') {
        newSchedule = self.initNewSchedule(schedule.uid, schedule.type, schedule.timestampStart, schedule.timestampEnd);
        fenceCopy.schedule.push(newSchedule);
      }
      else {
        newSchedule = self.initNewSchedule(schedule.uid, schedule.type, schedule.timestampStart, schedule.timestampEnd + 60000 - 1, schedule.dayOfWeek);
        fenceCopy.schedule.push(newSchedule);
      }
    });

    console.log('📊 Struttura coordinate finale:', JSON.stringify(fenceCopy.geometry.coordinates).substring(0, 200));

    if (fence.isNew || this.isField) {
      this.createGeofence(fenceCopy);
    } else {
      this.updateGeofence(fenceCopy);
    }

    // Reset della selezione dopo il salvataggio
    this.selectedItems = [];
  }


  updateGeofence(fence) {
    console.log('📝 Aggiornamento geofence:', fence.name, 'con colore:', JSON.parse(fence.drawConf)?.color);

    this.apiService.put(GEOFENCING_BY_CUSTOMER_ENDPOINT + "/" + this.selectedCustomerUidSelector$.uid + "/geofence", fence)
      .subscribe(
        () => {
          console.log('✅ Geofence aggiornato, ricaricamento lista...');
          
          // Pulisci il flag di modifica dopo il salvataggio
          this.clearGeofenceModifications(fence.uid);
          
          // Rimuovi il backup dopo il salvataggio riuscito
          this.removeGeofenceBackup(fence.uid);
          
          // Usa il metodo del servizio per ricaricare correttamente
          this.mapService.getGeofence();
        },
        error => {
          console.error('❌ Errore aggiornamento geofence:', error);
          this.utilsService.showErrorPopup(error);
        });
  }

  createGeofence(fence) {
    let drawConf = JSON.parse(fence.drawConf);
    const isImportedField = fence.name && fence.name.startsWith('Campo:');

    console.log('🆕 Creazione geofence:', fence.name, 'con colore:', drawConf?.color, 'Importato da campo:', isImportedField);
    console.log('🔍 Coordinate prima della modifica:', JSON.stringify(fence.geometry.coordinates).substring(0, 150));

    // 🔧 CORREZIONE: Gestione differenziata per geofence importati dai campi
    if (!this.isField && !isImportedField) {
      // Solo per geofence normali (non campi), rimuovi un livello
      fence.geometry.coordinates = fence.geometry.coordinates[0];
      console.log('🔧 Rimosso un livello di array per geofence normale');
    } else if (isImportedField) {
      // Per geofence importati dai campi, verifica se ci sono troppi livelli
      if (fence.geometry.coordinates[0] && fence.geometry.coordinates[0][0] && Array.isArray(fence.geometry.coordinates[0][0][0])) {
        // Troppi livelli, rimuovine uno
        fence.geometry.coordinates = fence.geometry.coordinates[0];
        console.log('🔧 Rimosso un livello extra per geofence importato da campo');
      } else {
        console.log('✅ Struttura coordinate già corretta per geofence importato');
      }
    }

    console.log('🔍 Coordinate dopo la modifica:', JSON.stringify(fence.geometry.coordinates).substring(0, 150));

    this.apiService.post(GEOFENCING_BY_CUSTOMER_ENDPOINT + "/" + this.selectedCustomerUidSelector$.uid + "/geofence", fence)
      .subscribe(
        (res: any) => {
          console.log('✅ Geofence creato, ricaricamento lista...');
          
          // Pulisci il flag di modifica dopo il salvataggio
          this.clearGeofenceModifications(fence.uid);
          
          // Rimuovi il backup dopo il salvataggio riuscito (se esistente)
          this.removeGeofenceBackup(fence.uid);
          
          // Usa il metodo del servizio per ricaricare correttamente
          this.mapService.getGeofence();
        },
        error => {
          console.error('❌ Errore creazione geofence:', error);
          this.utilsService.showErrorPopup(error);
        });
  }


    // getGeofence() {
    //   this.spinner.show();
    //   let self = this;
    //   this.isVisibleGeo = false;
    //   //this.mapService.removeAllPolygons();
    //   if (!this.mapService.polygonsGeofence) {
    //     this.mapService.polygonsGeofence = new Array();
    //   }
    //   if (this.isField) {
    //     this.mapService.removeAllPolygonsFields();
    //     this.isField = false;
    //   } else {
    //     this.mapService.removeAllPolygonsGeofence();
    //   }
    //   this.visibleGeofences = [];
    //   this.apiService.get(GEOFENCING_BY_CUSTOMER_ENDPOINT + "/" + this.selectedCustomerUidSelector$.uid)
    //     .subscribe(
    //       (geofence: any) => {
    //         this.visibleGeofences = geofence;
    //         _.forEach(this.visibleGeofences, function (visibleGeofence) {
    //           self.mapService.drawPolygonGeofence(visibleGeofence, false, true, true);
    //           visibleGeofence.drawConf = JSON.parse(visibleGeofence.drawConf);
    //           visibleGeofence.schedule = self.formatSchedules(visibleGeofence);
    //         });
    //         this.isVisibleGeo = true;
    //         this.spinner.hide();
    //       },
    //       error => {
    //         this.isVisibleGeo = true;
    //         this.spinner.hide();
    //         if (error.status === 403) {
    //         } else {
    //           this.utilsService.showErrorPopup(error);
    //         }
    //       });
    // }



      formatSchedules(fence) {
        let self = this;
        let newSchedule: any = null;
        let newSchedules = this.mergeShedules(fence.schedule);
        _.each(newSchedules, function (schedule) {
          if (schedule.type === 'Once') {
            schedule.from = self.utilsService.toUserTimezoneDateJS(schedule.timestampStart);
            schedule.to = self.utilsService.toUserTimezoneDateJS(schedule.timestampEnd);
            schedule.text = datepipe.transform(schedule.from, self.dateTimeFormatNoSecond) + ' - ' + datepipe.transform(schedule.to, self.dateTimeFormatNoSecond);
            fence.schedule.push(schedule);
          }
          else {
            //Check start and end are same day
            if (self.utilsService.sameDay(schedule.from, schedule.to)) {
              schedule.timestampEnd = schedule.timestampEnd - 60000 + 1;
              schedule.dayOfWeek = schedule.from.getDay();
              schedule.text = (self.translate.instant('SHORTDAY.' + DAYS[schedule.dayOfWeek])).toUpperCase() + ' ' + datepipe.transform(schedule.from, self.hoursFormat) + ' - ' + datepipe.transform(schedule.to, self.hoursFormat);
              fence.schedule.push(schedule);
            } else {//Split periods
              newSchedule = self.initNewSchedule(schedule.uid, schedule.type, 0, schedule.timestampEnd, schedule.to.getDay());
              newSchedule.text = (self.translate.instant('SHORTDAY.' + DAYS[newSchedule.dayOfWeek])).toUpperCase() + ' ' + datepipe.transform(0, self.hoursFormat) + ' - ' + datepipe.transform(newSchedule.to, self.hoursFormat);


              schedule.dayOfWeek = schedule.from.getDay();
              schedule.timestampEnd = moment.utc(schedule.timestampStart).endOf('day');
              schedule.to = self.utilsService.toUserTimezoneDateJS(schedule.timestampEnd);
              schedule.text = (self.translate.instant('SHORTDAY.' + DAYS[schedule.dayOfWeek])).toUpperCase() + ' ' + datepipe.transform(schedule.from, self.hoursFormat) + ' - ' + datepipe.transform(schedule.to, self.hoursFormat);
              fence.schedule.push(schedule);

              fence.schedule.push(newSchedule);

            }
          }
        });
        return newSchedules;
      }



        mergeShedules(schedules) {
          var newSchedules: any = [];
          let self = this;
          _.each(schedules, function (schedule) {
            if (!_.some(newSchedules, function (item: any) { return item.uid === schedule.uid; })) {
              if (!schedule.from && schedule.type === 'Weekly') {
                schedule.from = self.utilsService.fromUTC_toDateJS(self.hourDayToDatetime(schedule.timestampStart, schedule.dayOfWeek));
                schedule.to = self.utilsService.fromUTC_toDateJS(self.hourDayToDatetime(schedule.timestampEnd, schedule.dayOfWeek));
              }
              newSchedules.push(schedule);
            }
          });
          return newSchedules;
        }

  hourDayToDatetime(timestamp, weekOfDay) {
    var dayConst = 86400000;
    var firstDom = timestamp + (3 * dayConst);
    return firstDom + (weekOfDay * dayConst);
  }

 importFields() {
    console.log('📂 Importazione campi - caricamento lista senza disegno sulla mappa');
    this.isField = true;
    // Non attivare automaticamente editMode - l'utente dovrà selezionare manualmente un campo
    this.setFieldsList();
  }



  setFieldsList() {
    console.log('📋 Caricamento lista campi per importazione...');
    this.fieldsList = [];

    // Non rimuovere poligoni esistenti quando stiamo importando campi
    // if (!this.isField)
    //   this.mapService.removeAllPolygons();

    this.apiService.get(TRACKING_FIELDS_ENDPOINT.replace(':uidCustomer', this.selectedCustomerUidSelector$.uid))
      .subscribe(
        (fields: any) => {
          console.log(`📋 Caricati ${fields?.length || 0} campi dal server`);

          // Popola la lista dei campi disponibili per la selezione
          for (let field of fields) {
            // Parse della configurazione di disegno se è una stringa
            if (typeof field.drawConf === 'string') {
              try {
                field.drawConf = JSON.parse(field.drawConf);
              } catch (e) {
                console.warn('⚠️ Errore parsing drawConf per campo:', field.name, e);
                field.drawConf = { color: '#3388ff', mode: 'Polygon' };
              }
            }

            // Assicurati che il campo abbia le proprietà necessarie
            if (!field.drawConf) {
              field.drawConf = { color: '#3388ff', mode: 'Polygon' };
            }

            this.fieldsList.push(field);
          }

          console.log(`✅ Lista campi popolata con ${this.fieldsList.length} elementi - pronti per la selezione`);

          // Crea un geofence temporaneo per l'importazione dei campi che apparirà nell'accordion
          if (this.fieldsList.length > 0) {
            const importFieldGeofence = {

              name: 'Importa Campo',
              description: ``,
              isNew: true,
              isImportField: true, // Flag speciale per identificare questo tipo di geofence
              vehicles: [],
              inside: true,
              drawConf: {
                mode: 'ImportField',
                color: '#0066CC' // Blu per distinguerlo
              },
              schedule: [],
              geometry: null // Nessuna geometria finché non viene selezionato un campo
            };

            // Aggiungi il geofence di importazione alla lista visibile
            this.visibleGeofences.push(importFieldGeofence);
            console.log('📂 Geofence di importazione campo aggiunto all\'accordion');

            // Apri automaticamente l'accordion per mostrare la selezione dei campi
            setTimeout(() => {
              this.editGeofence(importFieldGeofence);
              console.log('📂 Accordion di importazione campo aperto automaticamente');
            }, 100);
          }

          // NON disegnare automaticamente i poligoni sulla mappa
          // L'utente dovrà selezionare manualmente un campo dalla lista
        },
        error => {
          console.error('❌ Errore nel caricamento dei campi:', error);
          this.utilsService.showErrorPopup(error);
        });
  }



  /**
   * Inizializza un nuovo schedule per un geofence
   */
  private initNewSchedule(uid: string, type: string, timestampStart: number, timestampEnd: number, dayOfWeek?: number): any {
    const newSchedule: any = {
      uid: uid,
      type: type,
      timestampStart: timestampStart,
      timestampEnd: timestampEnd
    };

    if (typeof dayOfWeek === 'number') {
      newSchedule.dayOfWeek = dayOfWeek;
    }

    return newSchedule;
  }


  setGeofenceTranslations() {
    this.timespanSelectBoxItems = this.createTranslatedOptionsForTimespanSelectBox();
    this.daysOfWeek = [
      { text: this.translate.instant('SHORTDAY.Mon'), value: false, original: 'Mon' },
      { text: this.translate.instant('SHORTDAY.Tue'), value: false, original: 'Tue' },
      { text: this.translate.instant('SHORTDAY.Wed'), value: false, original: 'Wed' },
      { text: this.translate.instant('SHORTDAY.Thu'), value: false, original: 'Thu' },
      { text: this.translate.instant('SHORTDAY.Fri'), value: false, original: 'Fri' },
      { text: this.translate.instant('SHORTDAY.Sat'), value: false, original: 'Sat' },
      { text: this.translate.instant('SHORTDAY.Sun'), value: false, original: 'Sun' },
    ];
    this.radioData = [
      {
        text: this.translate.instant('global.onVehicleOutside'),
        inside: true
      },
      {
        text: this.translate.instant('global.onVehicleInside'),
        inside: false
      }
    ];
  }

  createTranslatedOptionsForTimespanSelectBox() {
    var items: any[] = [];
    items.push({ id: 'daily', value: this.translate.instant('global.recurrent') });
    items.push({ id: 'timeperiod', value: this.translate.instant('global.oneoff') });
    return items;
  }

}
